import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import SafeIcon from '../common/SafeIcon'
import NuPitchBadge from '../components/NuPitchBadge/NuPitchBadge'
import { exportToPDF } from '../utils/pdfExport'
import * as FiIcons from 'react-icons/fi'

const { FiArrowLeft, FiArrowRight, FiX, FiMaximize, FiDownload, FiShare2 } = FiIcons

// Simple password hashing function (same as ShareModal)
const hashPassword = async (password) => {
  if (!password) return null
  try {
    const encoder = new TextEncoder()
    const data = encoder.encode(password.trim()) // Trim whitespace
    const hashBuffer = await crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    const hash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
    console.log('🔑 Password hashed for verification:', { originalLength: password.length, hashLength: hash.length })
    return hash
  } catch (error) {
    console.error('❌ Error hashing password:', error)
    return null
  }
}

const PresentationMode = ({ isPublic = false }) => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [slides, setSlides] = useState([])
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0)
  const [loading, setLoading] = useState(true)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showControls, setShowControls] = useState(true)
  const [projectData, setProjectData] = useState(null)
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [isExpired, setIsExpired] = useState(false)
  const [ownerRole, setOwnerRole] = useState(null)

  useEffect(() => {
    // Load slides from database
    const loadSlides = async () => {
      try {
        const { supabase } = await import('../lib/supabase')
        
        let projectQuery, slidesQuery
        
        if (isPublic) {
          // For public presentations, use the RPC function for public access
          console.log('🌐 Loading public presentation data for project:', id)
          
          // Try RPC function first for public project data
          let publicData, rpcError
          
          try {
            const rpcResult = await supabase
              .rpc('get_public_presentation', { project_uuid: id })
            publicData = rpcResult.data
            rpcError = rpcResult.error
          } catch (error) {
            console.warn('⚠️ RPC function not available, trying fallback method:', error)
            rpcError = error
          }
          
          // If RPC fails, try direct query as fallback
          if (rpcError || !publicData) {
            console.log('🔄 RPC failed, attempting direct public query...')
            
            // Create a custom header to bypass some RLS issues
            const supabasePublic = await import('../lib/supabase')
            const client = supabasePublic.supabase
            
            // Try a simpler approach - just get the project first
            let project, projectError
            try {
              // Add a small delay to ensure database consistency
              await new Promise(resolve => setTimeout(resolve, 100))
              
              // Try a simpler query first without the user join
              const result = await client
                .from('projects')
                .select('*')
                .eq('id', id)
                .maybeSingle()
              
              // If that works, get user info separately
              let userInfo = null
              if (result.data) {
                const userResult = await client
                  .from('users')
                  .select('id, role')
                  .eq('id', result.data.owner_id)
                  .maybeSingle()
                
                userInfo = userResult.data
              }
              
              project = result.data
              projectError = result.error
              
              console.log('📊 Project query result:', { 
                project: project, 
                projectError: projectError,
                hasPasswordHash: !!project?.password_hash,
                passwordHash: project?.password_hash, // Show actual hash for debugging
                passwordHashLength: project?.password_hash?.length,
                allowDownload: project?.allow_download,
                updatedAt: project?.updated_at,
                allProjectFields: Object.keys(project || {}),
                rawResult: result // Show the entire raw result
              })
              
              // Additional debug: specifically log the password_hash field
              console.log('🔍 Password hash debug:', {
                'project.password_hash': project?.password_hash,
                'result.data.password_hash': result?.data?.password_hash,
                'typeof password_hash': typeof project?.password_hash
              })
              
              // If password_hash is still undefined, do a direct test query
              if (!project?.password_hash) {
                console.log('🚨 Password hash is undefined, running direct test query...')
                try {
                  const testResult = await client
                    .from('projects')
                    .select('password_hash, title')
                    .eq('id', id)
                    .single()
                  
                  console.log('🧪 Direct test query result:', testResult)
                } catch (testError) {
                  console.log('🧪 Direct test query error:', testError)
                }
              }
              
              // If project exists, check if it's public
              if (project && project.status === 'published' && project.is_private === false) {
                console.log('✅ Found public project:', project.title)
                
                // Check if project has expired
                if (project.expires_at) {
                  const expirationDate = new Date(project.expires_at)
                  const now = new Date()
                  console.log('⏰ Checking expiration:', {
                    expiresAt: project.expires_at,
                    expirationDate: expirationDate.toISOString(),
                    now: now.toISOString(),
                    isExpired: now > expirationDate
                  })
                  if (now > expirationDate) {
                    console.log('❌ Project has expired:', expirationDate)
                    setIsExpired(true)
                    setLoading(false)
                    return
                  }
                }
                
                // Check if project requires password
                if (project.password_hash && !sessionStorage.getItem(`presentation_auth_${id}`)) {
                  console.log('🔒 Project requires password authentication')
                  setProjectData(project) // Set project data BEFORE showing password prompt
                  setOwnerRole(userInfo?.role || 'free') // userInfo is defined above
                  setShowPasswordPrompt(true)
                  setLoading(false)
                  return
                }
              } else if (project) {
                console.log('❌ Project exists but is not public:', { status: project.status, is_private: project.is_private })
                projectError = { message: 'Project is not public' }
                project = null
              } else {
                console.log('❌ No project found with ID:', id)
                projectError = { message: 'Project not found' }
              }
            } catch (error) {
              console.error('❌ Error in project query:', error)
              projectError = error
              project = null
            }
            
            if (projectError || !project) {
              console.error('❌ Cannot access public project:', id, projectError)
              setSlides([])
              setLoading(false)
              return
            }
            
            // Get user info for this project too
            let fallbackUserInfo = null
            try {
              const userResult = await client
                .from('users')
                .select('id, role')
                .eq('id', project.owner_id)
                .maybeSingle()
              fallbackUserInfo = userResult.data
            } catch (error) {
              console.log('⚠️ Could not load user info for fallback path:', error)
            }
            
            setProjectData(project)
            setOwnerRole(fallbackUserInfo?.role || 'free') // Set owner role from user query
            console.log('✅ Loaded public project via fallback:', project.title)
            
            // Try to load slides
            let slidesData, slidesError
            try {
              const result = await client
                .from('slides')
                .select(`
                  *,
                  elements(*)
                `)
                .eq('project_id', id)
                .order('slide_order')
              
              slidesData = result.data
              slidesError = result.error
              
              console.log('📊 Slides query result:', { count: slidesData?.length, slidesError })
            } catch (error) {
              console.error('❌ Error in slides query:', error)
              slidesError = error
              slidesData = null
            }
            
            if (slidesError) {
              console.error('❌ Error loading slides:', slidesError)
              // Don't return here - try to continue with empty slides
              slidesData = []
            }
            
            // Use the fallback slides data
            if (slidesData && slidesData.length > 0) {
              // Transform slides (use same logic as authenticated path)
              const transformedSlides = slidesData.map(slide => ({
                ...slide,
                elements: (slide.elements || []).map(element => ({
                  ...element,
                  type: element.element_type,
                  // Add the same transformation logic here...
                  ...(element.element_type === 'text' && {
                    content: (() => {
                      if (typeof element.content === 'string') return element.content
                      if (element.content?.text) {
                        if (typeof element.content.text === 'string') return element.content.text
                        if (Array.isArray(element.content.text)) return element.content.text.join(' ')
                        return String(element.content.text)
                      }
                      return element.content ? String(element.content) : ''
                    })(),
                  }),
                  ...(element.element_type === 'image' && {
                    src: element.content?.src || element.src || 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop',
                    content: ''
                  }),
                  ...(element.element_type === 'list' && {
                    content: (() => {
                      console.log('🔍 Debug list element in public presentation fallback:', element.id, 'content:', element.content, 'items:', element.content?.items, 'text:', element.content?.text)
                      
                      if (element.content?.items && Array.isArray(element.content.items)) {
                        return element.content.items
                      } else if (element.content?.text && Array.isArray(element.content.text)) {
                        return element.content.text
                      } else if (Array.isArray(element.content)) {
                        return element.content
                      } else if (typeof element.content === 'string') {
                        try {
                          return element.content.split(',').map(item => item.trim())
                        } catch (e) {
                          return [element.content]
                        }
                      } else {
                        console.warn('⚠️ Unknown list content format in public presentation fallback:', element.content)
                        return []
                      }
                    })(),
                  }),
                  ...(element.element_type === 'video' && {
                    videoUrl: element.content?.videoUrl || element.videoUrl || 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
                    autoplay: element.content?.autoplay || element.autoplay || false,
                    content: ''
                  }),
                  ...(element.element_type === 'shape' && {
                    content: '',
                    shape: element.content?.shape || element.shape || 'rectangle'
                  })
                }))
              }))
              
              console.log('🔄 Transformed fallback slides:', transformedSlides)
              setSlides(transformedSlides)
            } else {
              setSlides([])
            }
            
            setLoading(false)
            return
          }
          
          if (!publicData || !publicData.project) {
            console.error('❌ Public project not found or not published:', id)
            setSlides([])
            setLoading(false)
            return
          }
          
          // Set the project data and slides from RPC response
          setProjectData(publicData.project)
          setOwnerRole(publicData.project.users?.role || 'free') // Set owner role, default to free
          console.log('✅ Loaded public project:', publicData.project.title)
          
          if (publicData.slides && publicData.slides.length > 0) {
            // Transform the slides data (same as authenticated path)
            const transformedSlides = publicData.slides.map(slide => ({
              ...slide,
              elements: (slide.elements || []).map(element => ({
                ...element,
                type: element.element_type, // Transform element_type to type
                // Handle different element types properly
                ...(element.element_type === 'text' && {
                  content: (() => {
                    // Handle text content transformation robustly
                    if (typeof element.content === 'string') {
                      return element.content
                    } else if (element.content?.text) {
                      if (typeof element.content.text === 'string') {
                        return element.content.text
                      } else if (Array.isArray(element.content.text)) {
                        return element.content.text.join(' ')
                      } else {
                        return String(element.content.text)
                      }
                    } else if (typeof element.content === 'object' && element.content !== null) {
                      console.warn('⚠️ Text element with object content but no text property:', element.id, element.content)
                      return ''
                    } else {
                      return element.content ? String(element.content) : ''
                    }
                  })(),
                }),
                ...(element.element_type === 'image' && {
                  src: element.content?.src || element.src || 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop',
                  content: ''
                }),
                ...(element.element_type === 'link' && {
                  content: element.content?.text || element.content || '',
                  href: element.content?.href || element.href || ''
                }),
                ...(element.element_type === 'list' && {
                  content: (() => {
                    if (element.content?.items && Array.isArray(element.content.items)) {
                      return element.content.items
                    } else if (element.content?.text && Array.isArray(element.content.text)) {
                      return element.content.text
                    } else if (Array.isArray(element.content)) {
                      return element.content
                    } else if (typeof element.content === 'string') {
                      try {
                        return element.content.split(',').map(item => item.trim())
                      } catch (e) {
                        return [element.content]
                      }
                    } else {
                      return []
                    }
                  })(),
                }),
                ...(element.element_type === 'video' && {
                  videoUrl: element.content?.videoUrl || element.videoUrl || 'https://www.youtube.com/watch?v=dQw4w9WgXcQ',
                  autoplay: element.content?.autoplay || element.autoplay || false,
                  content: ''
                }),
                ...(element.element_type === 'shape' && {
                  content: '',
                  shape: element.content?.shape || element.shape || 'rectangle'
                })
              }))
            }))
            
            console.log('🔄 Transformed public slides for presentation:', transformedSlides)
            setSlides(transformedSlides)
          } else {
            console.log('⚠️ No slides found for public presentation:', id)
            setSlides([])
          }
          
          setLoading(false)
          return
        } else {
          // For authenticated users, use normal queries
          projectQuery = supabase
            .from('projects')
            .select(`
              *,
              users!projects_owner_id_fkey(id, role)
            `)
            .eq('id', id)
            .single()
            
          slidesQuery = supabase
            .from('slides')
            .select(`
              *,
              elements(*)
            `)
            .eq('project_id', id)
            .order('slide_order')
        }
        
        // Load project data first
        const { data: project, error: projectError } = await projectQuery
        
        if (projectError) {
          console.error('Error loading project:', projectError)
          if (isPublic) {
            // For public access, show a more specific error
            console.error('❌ Public project not found or not published:', id)
            setSlides([])
            setLoading(false)
            return
          } else {
            setSlides([])
            setLoading(false)
            return
          }
        } else {
          setProjectData(project)
          setOwnerRole(project.users?.role || 'free') // Set owner role, default to free
          console.log(`✅ Loaded ${isPublic ? 'public' : 'private'} project:`, project.title)
        }
        
        // Load slides from database
        const { data: slidesData, error } = await slidesQuery

        if (error) {
          console.error('Error loading slides for presentation:', error)
          setSlides([])
          setLoading(false)
          return
        }

        if (slidesData && slidesData.length > 0) {
          console.log('✅ Loaded slides for presentation:', slidesData)
          
          // Transform database format to UI format (same as SlideEditor)
          const transformedSlides = slidesData.map(slide => ({
            ...slide,
            elements: (slide.elements || []).map(element => ({
              ...element,
              type: element.element_type, // Transform element_type to type
              // Handle different element types properly
              ...(element.element_type === 'text' && {
                content: (() => {
                  // Handle text content transformation robustly
                  if (typeof element.content === 'string') {
                    return element.content
                  } else if (element.content?.text) {
                    if (typeof element.content.text === 'string') {
                      return element.content.text
                    } else if (Array.isArray(element.content.text)) {
                      return element.content.text.join(' ')
                    } else {
                      return String(element.content.text)
                    }
                  } else if (typeof element.content === 'object' && element.content !== null) {
                    // If content is an object but doesn't have a text property, return empty string
                    // to prevent [object Object] from appearing
                    console.warn('⚠️ Text element with object content but no text property:', element.id, element.content)
                    return ''
                  } else {
                    return element.content ? String(element.content) : ''
                  }
                })(),
              }),
              ...(element.element_type === 'image' && {
                src: element.content?.src || element.src || 'https://images.unsplash.com/photo-1566438480900-0609be27a4be?w=400&h=300&fit=crop', // Extract image src
                content: '' // Images don't need content
              }),
              ...(element.element_type === 'link' && {
                content: element.content?.text || element.content || '',
                href: element.content?.href || element.href || ''
              }),
              ...(element.element_type === 'list' && {
                content: (() => {
                  console.log('🔍 Debug list element in presentation:', element.id, 'content:', element.content, 'items:', element.content?.items, 'text:', element.content?.text)
                  
                  // Handle different list content formats
                  if (element.content?.items && Array.isArray(element.content.items)) {
                    return element.content.items
                  } else if (element.content?.text && Array.isArray(element.content.text)) {
                    // Handle legacy format where list items are stored as {text: [...]}
                    return element.content.text
                  } else if (Array.isArray(element.content)) {
                    return element.content
                  } else if (typeof element.content === 'string') {
                    try {
                      return element.content.split(',').map(item => item.trim())
                    } catch (e) {
                      return [element.content]
                    }
                  } else {
                    console.warn('⚠️ Unknown list content format in presentation:', element.content)
                    return []
                  }
                })(),
              }),
              ...(element.element_type === 'video' && {
                videoUrl: element.content?.videoUrl || element.videoUrl || 'https://www.youtube.com/watch?v=dQw4w9WgXcQ', // Extract video URL
                autoplay: element.content?.autoplay || element.autoplay || false, // Extract autoplay setting
                content: '' // Videos don't need content string
              }),
              ...(element.element_type === 'shape' && {
                content: '', // Shapes don't need content
                shape: element.content?.shape || element.shape || 'rectangle' // Extract shape type
              })
            }))
          }))
          
          console.log('🔄 Transformed slides for presentation:', transformedSlides)
          setSlides(transformedSlides)
        } else {
          console.log('⚠️ No slides found for presentation:', id)
          // Show empty state instead of mock data
          setSlides([])
        }
      } catch (error) {
        console.error('Error in loadSlides:', error)
        setSlides([])
      }
      
      setLoading(false)
    }

    if (id) {
      loadSlides()
    }
  }, [id])

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        nextSlide()
      } else if (e.key === 'ArrowLeft') {
        prevSlide()
      } else if (e.key === 'Escape') {
        exitPresentation()
      } else if (e.key === 'f' || e.key === 'F') {
        toggleFullscreen()
      }
    }

    const handleMouseMove = () => {
      setShowControls(true)
      clearTimeout(window.hideControlsTimeout)
      window.hideControlsTimeout = setTimeout(() => {
        setShowControls(false)
      }, 3000)
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('mousemove', handleMouseMove)
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('mousemove', handleMouseMove)
      clearTimeout(window.hideControlsTimeout)
    }
  }, [currentSlideIndex, slides.length])

  const nextSlide = () => {
    if (currentSlideIndex < slides.length - 1) {
      setCurrentSlideIndex(currentSlideIndex + 1)
    }
  }

  const prevSlide = () => {
    if (currentSlideIndex > 0) {
      setCurrentSlideIndex(currentSlideIndex - 1)
    }
  }

  const exitPresentation = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen()
    }
    if (isPublic) {
      // For public presentations, go to a generic page or close window
      window.close()
    } else {
      navigate(`/editor/${id}`)
    }
  }

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen()
      setIsFullscreen(true)
    } else {
      document.exitFullscreen()
      setIsFullscreen(false)
    }
  }

  const handleExport = async () => {
    try {
      if (!slides || slides.length === 0) {
        alert('No slides available to export')
        return
      }

      // Show loading indicator
      const downloadBtn = document.querySelector('[data-download-btn]')
      if (downloadBtn) {
        downloadBtn.style.opacity = '0.5'
        downloadBtn.style.pointerEvents = 'none'
      }

      await exportToPDF(slides, projectData?.title || 'Presentation')
      
      // Success feedback
      alert('PDF downloaded successfully!')
    } catch (error) {
      console.error('Export failed:', error)
      alert('Failed to export PDF. Please try again.')
    } finally {
      // Reset button state
      const downloadBtn = document.querySelector('[data-download-btn]')
      if (downloadBtn) {
        downloadBtn.style.opacity = '1'
        downloadBtn.style.pointerEvents = 'auto'
      }
    }
  }

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href)
    alert('Presentation link copied to clipboard!')
  }

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setPasswordError('Please enter a password')
      return
    }

    try {
      const hashedPassword = await hashPassword(password.trim())
      const storedHash = projectData?.password_hash
      
      console.log('🔍 ProjectData state check:', {
        projectData: projectData,
        projectDataPasswordHash: projectData?.password_hash,
        projectDataKeys: Object.keys(projectData || {})
      })
      
      console.log('🔐 Password verification:', {
        enteredPassword: password.trim(),
        enteredPasswordLength: password.trim().length,
        generatedHash: hashedPassword,
        generatedHashLength: hashedPassword?.length,
        storedHash: storedHash,
        storedHashLength: storedHash?.length,
        hashesMatch: hashedPassword === storedHash,
        projectData: projectData
      })
      
      if (hashedPassword && storedHash && hashedPassword === storedHash) {
        console.log('✅ Password verification successful')
        // Password correct, store authentication and reload
        sessionStorage.setItem(`presentation_auth_${id}`, 'true')
        setShowPasswordPrompt(false)
        setPasswordError('')
        setPassword('')
        
        // Reload the presentation
        window.location.reload()
      } else {
        console.log('❌ Password verification failed')
        setPasswordError('Incorrect password. Please try again.')
      }
    } catch (error) {
      console.error('❌ Error verifying password:', error)
      setPasswordError('Error verifying password. Please try again.')
    }
  }

  // Show password prompt if required
  if (showPasswordPrompt) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">Password Required</h2>
          <p className="text-gray-600 mb-6 text-center">This presentation is password protected. Please enter the password to continue.</p>
          
          <div className="space-y-4">
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              onKeyPress={(e) => e.key === 'Enter' && handlePasswordSubmit()}
            />
            
            {passwordError && (
              <p className="text-red-600 text-sm">{passwordError}</p>
            )}
            
            <button
              onClick={handlePasswordSubmit}
              className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
            >
              Access Presentation
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show expiration message if expired
  if (isExpired) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center text-white">
          <div className="mb-6">
            <svg className="w-16 h-16 mx-auto mb-4 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-3xl font-bold mb-4">Presentation Expired</h2>
          <p className="text-xl text-gray-300 mb-8">This presentation is no longer available as it has expired.</p>
          <button
            onClick={() => window.close()}
            className="px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-black">
        <div className="text-center text-white">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Loading presentation...</p>
        </div>
      </div>
    )
  }

  const currentSlide = slides[currentSlideIndex]

  return (
    <div className="h-screen bg-black flex flex-col relative">
      {/* Controls */}
      <div className={`absolute top-4 left-4 right-4 z-50 flex justify-between items-center transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0'
      }`}>
        <div className="flex items-center space-x-4">
          <button
            onClick={exitPresentation}
            className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
          <div className="text-white text-sm bg-black bg-opacity-50 px-3 py-1 rounded">
            {projectData && (
              <div className="text-xs text-gray-300 mb-1">{projectData.title}</div>
            )}
            {currentSlideIndex + 1} / {slides.length}
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={handleShare}
            className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiShare2} className="w-5 h-5" />
          </button>
          {/* Only show download button if allowed by project settings */}
          {(!isPublic || projectData?.allow_download) && (
            <button
              onClick={handleExport}
              data-download-btn
              className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-lg transition-colors"
              title="Download as PDF"
            >
              <SafeIcon icon={FiDownload} className="w-5 h-5" />
            </button>
          )}
          <button
            onClick={toggleFullscreen}
            className="p-2 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiMaximize} className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Main Slide */}
      <div className="flex-1 flex items-center justify-center p-8">
        {currentSlide ? (
          <div 
            className="w-full max-w-6xl aspect-video relative rounded-lg overflow-hidden shadow-2xl"
            style={{
              background: currentSlide.background?.type === 'gradient' 
                ? currentSlide.background.value
                : currentSlide.background?.type === 'image' 
                  ? `url(${currentSlide.background.url}) center/cover`
                  : currentSlide.background?.value || '#ffffff'
            }}
          >
            {/* Overlay for readability when using image background */}
            {currentSlide.background?.type === 'image' && (
              <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.25)' }} />
            )}
            {currentSlide.elements && currentSlide.elements.map((element) => (
            <div
              key={element.id}
              className="absolute"
              style={{
                left: `${(element.style.position.x / 800) * 100}%`,
                top: `${(element.style.position.y / 600) * 100}%`,
                width: `${(element.style.width / 800) * 100}%`,
                height: `${(element.style.height / 600) * 100}%`,
                fontSize: `${parseInt(element.style.fontSize) * 1.2}px`,
                color: currentSlide.background?.type === 'image' ? (element.style.color || '#ffffff') : element.style.color,
                textAlign: element.style.textAlign,
                fontWeight: element.style.fontWeight,
                lineHeight: element.style.lineHeight || '1.4',
                display: 'flex',
                alignItems: element.type === 'text' ? 'center' : 'flex-start',
                justifyContent: element.style.textAlign === 'center' ? 'center' : 
                              element.style.textAlign === 'right' ? 'flex-end' : 'flex-start',
                transform: `rotate(${element.style.rotation || 0}deg)`, // Add rotation support
                transformOrigin: 'center center',
                zIndex: element.style.zIndex || 0 // Add z-index support
              }}
            >
              {element.type === 'text' && element.content}
              {element.type === 'link' && (
                <a
                  href={element.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-white underline hover:opacity-80 transition-opacity cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {element.content}
                </a>
              )}
              {element.type === 'list' && (
                <ul className="w-full space-y-2">
                  {Array.isArray(element.content) ? element.content.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="mr-3">•</span>
                      <span>{item}</span>
                    </li>
                  )) : <li className="flex items-start"><span className="mr-3">•</span><span>{element.content}</span></li>}
                </ul>
              )}
              {element.type === 'image' && (
                <img
                  src={element.src}
                  alt="Slide element"
                  className="w-full h-full object-cover"
                  style={{ borderRadius: element.style.borderRadius }}
                />
              )}
              {element.type === 'video' && (() => {
                // Helper function to extract YouTube video ID from URL
                const getYouTubeVideoId = (url) => {
                  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/)
                  return match ? match[1] : null
                }

                const videoId = getYouTubeVideoId(element.videoUrl || '')

                return (
                  <div className="w-full h-full bg-black relative">
                    {videoId ? (
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=${element.autoplay ? '1' : '0'}&controls=1&modestbranding=1&rel=0`}
                        title="YouTube video player"
                        className="w-full h-full"
                        style={{ borderRadius: element.style.borderRadius }}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-white">
                        <div className="text-center">
                          <svg className="w-16 h-16 mx-auto mb-2" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
                          </svg>
                          <p>No video URL</p>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })()}
              {element.type === 'shape' && (
                <div
                  className="w-full h-full"
                  style={{
                    backgroundColor: element.style.backgroundColor || '#3b82f6',
                    borderRadius: element.style.shapeType === 'circle' ? '50%' : element.style.borderRadius || '0px',
                    clipPath: element.style.shapeType === 'triangle' ? 'polygon(50% 0%, 0% 100%, 100% 100%)' : 'none'
                  }}
                />
              )}
            </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-white">
            <h2 className="text-2xl mb-4">No slides found</h2>
            <p>This project doesn't have any slides to present.</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <div className={`absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center space-x-4 transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0'
      }`}>
        <button
          onClick={prevSlide}
          disabled={currentSlideIndex === 0}
          className="p-3 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon icon={FiArrowLeft} className="w-6 h-6" />
        </button>
        
        <div className="flex space-x-2">
          {slides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlideIndex(index)}
              className={`w-3 h-3 rounded-full transition-colors ${
                index === currentSlideIndex ? 'bg-white' : 'bg-white bg-opacity-30'
              }`}
            />
          ))}
        </div>
        
        <button
          onClick={nextSlide}
          disabled={currentSlideIndex === slides.length - 1}
          className="p-3 bg-black bg-opacity-50 hover:bg-opacity-70 text-white rounded-full disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <SafeIcon icon={FiArrowRight} className="w-6 h-6" />
        </button>
      </div>

      {/* Instructions */}
      <div className={`absolute bottom-4 left-4 text-white text-sm bg-black bg-opacity-50 px-3 py-2 rounded transition-opacity duration-300 ${
        showControls ? 'opacity-100' : 'opacity-0'
      }`}>
        Arrow keys/Space: Navigate • F: Fullscreen • ESC: Exit
      </div>

      {/* NuPitch Badge for FREE accounts on public presentations */}
      {isPublic && ownerRole === 'free' && <NuPitchBadge />}
    </div>
  )
}

export default PresentationMode